'use client';

import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import styles from './HeroGlobe.module.css';
import { useTranslations } from 'next-intl';

const pointVertexShader = `
  attribute float aType;        // 1.0 = land, 0.0 = ocean
  attribute float aIsFlag;      // 1.0 = flag tinted (Sudan or Malaysia), 0.0 = standard
  attribute vec3 aFlagColor;    // RGB color of the flag dot
  uniform float uPixelRatio;
  uniform float uBaseSize;
  uniform float uTime;

  varying vec3 vWorldNormal;
  varying vec3 vWorldPosition;
  varying float vType;
  varying float vIsFlag;
  varying vec3 vFlagColor;
  varying float vMerge;

  void main(){
    vType = aType;
    vIsFlag = aIsFlag;
    vFlagColor = aFlagColor;

    // Subtle breathing along the surface normal. Flag dots sit this out so
    // Sudan and Malaysia stay crisp, and ocean moves slightly more than land
    // so the water reads as less solid. Amplitude stays tiny on purpose: much
    // larger and the geography starts to look like it is boiling.
    float breathWave = sin(
      uTime * 1.1 + position.x * 3.4 + position.z * 2.7 + position.y * 1.9
    );
    float breathAmp = aIsFlag > 0.5 ? 0.0 : (aType > 0.5 ? 0.006 : 0.011);

    vec3 displaced = position + normal * breathWave * breathAmp;

    vec4 worldPosition = modelMatrix * vec4(displaced, 1.0);
    vec4 mvPosition = viewMatrix * worldPosition;
    gl_Position = projectionMatrix * mvPosition;

    vWorldPosition = worldPosition.xyz;
    vWorldNormal = normalize((modelMatrix * vec4(normal, 0.0)).xyz);

    float dist = max(1.5, length(mvPosition.xyz));
    float sizeScale = aIsFlag > 0.5 ? 1.15 : (aType > 0.5 ? 1.0 : 0.60);
    float size = uBaseSize * sizeScale / pow(dist, 0.68);

    // Flag dots fuse into a solid flag as the camera closes in, and break back
    // apart into a dot grid as it pulls out. 1.0 = solid, 0.0 = dotted.
    // Spacing on screen scales as 1/dist, so the dots must outrun that to close
    // the gaps: hence the extra gain on top of the standard size falloff.
    float merge = aIsFlag > 0.5 ? (1.0 - smoothstep(3.2, 6.8, dist)) : 0.0;
    size *= mix(1.0, 3.4, merge);
    vMerge = merge;

    gl_PointSize = clamp(size * uPixelRatio, 1.5, 64.0);
  }
`;

const pointFragmentShader = `
  uniform vec3 uLandColor;
  uniform vec3 uOceanColor;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPosition;
  varying float vType;
  varying float vIsFlag;
  varying vec3 vFlagColor;
  varying float vMerge;

  void main(){
    vec2 coord = gl_PointCoord - vec2(0.5);
    float dist = length(coord);

    // Circular disc normally. Merging flag dots square off toward the full
    // sprite quad, because circles on a square grid leave corner gaps no
    // matter how large they get.
    float disc = smoothstep(0.5, 0.42, dist);
    float alpha = mix(disc, 1.0, vMerge);
    if (alpha < 0.01) discard;

    // Camera facing test: culls back hemisphere so rear continents don't show through oceans
    vec3 viewDir = normalize(cameraPosition - vWorldPosition);
    float facing = dot(normalize(vWorldNormal), viewDir);
    if (facing < -0.05) discard;

    // Subtle edge shading across curvature for 3D depth
    float edgeShading = smoothstep(-0.05, 0.35, facing);

    // If point is a flag dot, render its flag color; otherwise use land or ocean theme color
    vec3 col = vIsFlag > 0.5 ? vFlagColor : (vType > 0.5 ? uLandColor : uOceanColor);

    // Flag dots always retain full opacity (1.0) regardless of day/night mode
    float typeAlpha = (vIsFlag > 0.5 || vType > 0.5) ? 1.0 : 0.18;

    gl_FragColor = vec4(col, alpha * edgeShading * typeAlpha);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

// Subtle luminous horizon rim shader to cleanly outline the globe boundary in space
const rimVertexShader = `
  varying vec3 vWorldNormal;
  varying vec3 vWorldPosition;

  void main(){
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * viewMatrix * worldPosition;
    vWorldPosition = worldPosition.xyz;
    vWorldNormal = normalize((modelMatrix * vec4(normal, 0.0)).xyz);
  }
`;

const rimFragmentShader = `
  uniform vec3 uColor;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPosition;

  void main(){
    vec3 viewDir = normalize(cameraPosition - vWorldPosition);
    float facing = abs(dot(normalize(vWorldNormal), viewDir));

    float rim = pow(1.0 - facing, 4.5);
    if (rim < 0.01) discard;

    gl_FragColor = vec4(uColor, rim * 0.45);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

// ===================================================================
// FLIGHT CONNECTION ARC SHADERS
// ===================================================================
const arcVertexShader = `
  varying vec2 vArcUv;
  varying vec3 vArcNormal;
  varying vec3 vArcPos;
  void main(){
    vArcUv = uv;
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vArcNormal = (modelMatrix * vec4(normal, 0.0)).xyz;
    vArcPos = worldPos.xyz;
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
`;

const arcCoreFragmentShader = `
  uniform float uProgress;
  uniform float uTime;
  uniform vec3 uColor;
  uniform float uIntensity;
  varying vec2 vArcUv;
  varying vec3 vArcPos;

  void main(){
    if (vArcUv.x > uProgress) discard;

    // Ray-sphere occlusion: occlude fragments hidden behind the solid globe
    vec3 rayDir = vArcPos - cameraPosition;
    float rayLen = length(rayDir);
    rayDir /= rayLen;

    float t = -dot(cameraPosition, rayDir);
    float occlusionFade = 1.0;
    if (t > 0.0 && t < rayLen) {
      vec3 closestPoint = cameraPosition + t * rayDir;
      float distToCenter = length(closestPoint);
      // Smoothly fade out as the arc dips behind the globe radius (2.0)
      occlusionFade = smoothstep(1.94, 2.00, distToCenter);
    }
    if (occlusionFade <= 0.001) discard;

    // Both ends taper out gracefully as the arc touches the surface
    float taper = smoothstep(0.0, 0.12, vArcUv.x) * (1.0 - smoothstep(0.88, 1.0, vArcUv.x));

    // A bright energy pulse travels along the arc from Khartoum to Johor on a loop
    float head = fract(uTime * 0.28);
    float d = vArcUv.x - head;
    float pulse = exp(-abs(d) * 26.0) + exp(-abs(d + 1.0) * 26.0);

    float alpha = taper * (0.55 + pulse * 0.9) * uIntensity * occlusionFade;
    gl_FragColor = vec4(uColor * (1.0 + pulse * 1.4), alpha);
  }
`;

const arcGlowFragmentShader = `
  uniform float uProgress;
  uniform float uTime;
  uniform vec3 uColor;
  uniform float uIntensity;
  varying vec2 vArcUv;
  varying vec3 vArcNormal;
  varying vec3 vArcPos;

  void main(){
    if (vArcUv.x > uProgress) discard;

    // Ray-sphere occlusion: occlude fragments hidden behind the solid globe
    vec3 rayDir = vArcPos - cameraPosition;
    float rayLen = length(rayDir);
    rayDir /= rayLen;

    float t = -dot(cameraPosition, rayDir);
    float occlusionFade = 1.0;
    if (t > 0.0 && t < rayLen) {
      vec3 closestPoint = cameraPosition + t * rayDir;
      float distToCenter = length(closestPoint);
      occlusionFade = smoothstep(1.94, 2.00, distToCenter);
    }
    if (occlusionFade <= 0.001) discard;

    float taper = smoothstep(0.0, 0.12, vArcUv.x) * (1.0 - smoothstep(0.88, 1.0, vArcUv.x));

    vec3 viewDir = normalize(vArcPos - cameraPosition);
    float facing = abs(dot(normalize(vArcNormal), viewDir));
    float radial = pow(facing, 1.4);

    float head = fract(uTime * 0.28);
    float d = vArcUv.x - head;
    float pulse = exp(-abs(d) * 26.0) + exp(-abs(d + 1.0) * 26.0);

    float alpha = taper * radial * (0.55 + pulse * 0.9) * uIntensity * occlusionFade;
    gl_FragColor = vec4(uColor * (1.0 + pulse * 1.4), alpha);
  }
`;

const RESUME_DELAY = 1800;
const GLOBE_R = 2;
const ARC_SEGMENTS = 200;

const KHARTOUM = { lat: 15.5, lon: 32.5 };
const JOHOR_UTM = { lat: 1.56, lon: 103.63 };

// --- Intro sequence timeline (seconds) ---
const T_HOLD = 1.0;      // tight on Sudan
const T_PULLBACK = 2.2;  // zoom out to full globe
const T_TURN = 1.9;      // rotate from Sudan toward the Sudan<->Malaysia midpoint
const T_ARC = 1.6;       // arc draws itself
const T1 = T_HOLD;
const T2 = T1 + T_PULLBACK;
const T3 = T2 + T_TURN;
const T4 = T3 + T_ARC;

const CAM_NEAR = new THREE.Vector3(0, 0, 3.4);
const CAM_FAR = new THREE.Vector3(0, 0, 10);

const easeInOutCubic = (x: number) =>
  x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
const easeInOutSine = (x: number) => -(Math.cos(Math.PI * x) - 1) / 2;
const easeOutExpo = (x: number) => (x >= 1 ? 1 : 1 - Math.pow(2, -10 * x));

// ===================================================================
// COUNTRY BOUNDARY OUTLINES
// ===================================================================
const SUDAN_POLY: [number, number][] = [
  [10.919, 22.861], [11.251, 22.954], [11.396, 22.915], [11.633, 22.542], [12.073, 22.612],
  [12.03, 22.458], [12.611, 22.446], [12.743, 22.205], [12.64, 21.936], [12.794, 21.809],
  [13.098, 21.964], [13.335, 22.268], [13.78, 22.075], [14.14, 22.546], [14.544, 22.364],
  [14.689, 22.677], [14.857, 22.659], [15.202, 22.958], [15.541, 22.907], [15.704, 23.095],
  [15.721, 23.984], [19.995, 23.981], [20.002, 24.981], [21.995, 24.981], [21.994, 31.248],
  [22.227, 31.423], [21.995, 31.435], [21.995, 33.181], [21.709, 33.561], [21.767, 33.999],
  [21.995, 34.084], [21.996, 36.884], [21.65, 36.888], [21.058, 37.313], [21.006, 37.252],
  [21.208, 37.105], [19.576, 37.244], [18.862, 37.434], [18.014, 38.604], [17.526, 38.233],
  [17.312, 37.496], [17.03, 37.403], [17.063, 36.973], [16.514, 36.877], [16.278, 36.954],
  [15.133, 36.425], [14.264, 36.526], [12.721, 36.124], [12.659, 35.688], [11.819, 35.073],
  [10.903, 34.958], [10.729, 34.839], [10.684, 34.754], [10.884, 34.575], [10.566, 34.279],
  [10.224, 34.326], [9.455, 34.071], [9.499, 33.881], [10.175, 33.917], [10.739, 33.14],
  [11.599, 33.083], [12.21, 33.209], [12.216, 32.732], [12.003, 32.747], [12.0, 32.082],
  [11.703, 32.348], [11.082, 32.431], [9.764, 31.164], [9.724, 30.765], [10.27, 30.013],
  [10.058, 29.615], [9.762, 29.483], [9.529, 28.914], [9.325, 28.844], [9.331, 28.045],
  [9.595, 27.895], [9.607, 27.08], [9.481, 26.626], [10.181, 25.905], [10.42, 25.828],
  [10.287, 25.077], [9.967, 24.976], [9.819, 24.796], [8.887, 24.558], [8.682, 24.114],
  [8.733, 23.49], [8.975, 23.567], [9.019, 23.436], [9.278, 23.632], [9.863, 23.645],
  [10.919, 22.861]
];

const MALAYSIA_PEN: [number, number][] = [
  [6.258, 102.073], [6.177, 102.338], [5.872, 102.521], [5.486, 103.028], [4.807, 103.448],
  [4.306, 103.496], [3.758, 103.339], [3.512, 103.482], [2.922, 103.441], [2.672, 103.638],
  [2.649, 103.765], [2.34, 103.949], [1.638, 104.255], [1.367, 104.277], [1.375, 104.106],
  [1.485, 104.059], [1.545, 104.148], [1.506, 104.053], [1.648, 103.966], [1.449, 104.01],
  [1.451, 103.698], [1.353, 103.6], [1.382, 103.548], [1.27, 103.516], [1.538, 103.367],
  [1.846, 102.836], [1.859, 102.699], [2.058, 102.545], [2.236, 102.154], [2.395, 101.989],
  [2.395, 101.865], [2.587, 101.774], [2.85, 101.286], [3.002, 101.373], [3.275, 101.298],
  [3.622, 101.041], [3.787, 100.812], [3.851, 100.845], [3.883, 100.709], [3.984, 100.706],
  [4.019, 100.871], [4.155, 100.633], [4.319, 100.565], [4.56, 100.646], [4.611, 100.594],
  [4.67, 100.666], [4.666, 100.604], [4.793, 100.617], [4.923, 100.442], [5.084, 100.365],
  [5.211, 100.433], [5.584, 100.351], [5.811, 100.376], [6.013, 100.349], [6.381, 100.137],
  [6.695, 100.167], [6.696, 100.274], [6.522, 100.387], [6.445, 100.631], [6.493, 100.73],
  [6.237, 100.833], [6.246, 101.082], [5.938, 101.079], [5.792, 100.965], [5.63, 101.122],
  [5.787, 101.249], [5.911, 101.558], [5.766, 101.666], [5.74, 101.8], [6.095, 102.06],
  [6.258, 102.073]
];

const MALAYSIA_BORNEO: [number, number][] = [
  [4.909, 115.146], [5.067, 115.557], [5.212, 115.605], [5.307, 115.365], [5.413, 115.393],
  [5.636, 115.605], [5.52, 115.605], [5.56, 115.849], [7.027, 116.777], [6.592, 116.784],
  [7.003, 117.15], [6.636, 117.296], [6.451, 117.727], [6.198, 117.6], [5.973, 117.669],
  [5.869, 117.469], [6.061, 118.017], [5.864, 118.125], [5.809, 117.921], [5.687, 117.96],
  [5.814, 118.365], [5.682, 118.594], [5.526, 118.565], [5.636, 118.651], [5.394, 118.933],
  [5.345, 119.278], [5.106, 119.156], [4.944, 118.681], [5.039, 118.357], [4.882, 118.136],
  [4.519, 118.6], [4.364, 118.557], [4.231, 117.983], [4.424, 117.656], [4.16, 117.567],
  [4.356, 117.203], [4.379, 116.126], [4.271, 116.021], [4.389, 115.844], [3.921, 115.539],
  [3.43, 115.601], [3.031, 115.464], [3.042, 115.242], [2.833, 115.069], [2.609, 115.056],
  [2.51, 115.206], [2.247, 114.779], [1.903, 114.832], [1.841, 114.68], [1.435, 114.546],
  [1.442, 113.91], [1.228, 113.625], [1.416, 112.958], [1.57, 112.973], [1.568, 112.47],
  [1.435, 112.162], [1.143, 112.093], [1.008, 111.823], [1.075, 111.198], [0.851, 110.554],
  [1.597, 109.658], [1.89, 109.528], [2.083, 109.645], [1.692, 109.928], [1.674, 110.291],
  [1.803, 110.329], [1.547, 110.727], [1.449, 110.678], [1.572, 110.793], [1.372, 111.142],
  [1.395, 111.233], [1.333, 111.341], [1.354, 111.384], [1.584, 111.006], [1.627, 111.267],
  [1.774, 111.096], [2.162, 111.378], [2.145, 111.185], [2.416, 111.221], [2.346, 111.493],
  [2.706, 111.458], [3.162, 113.008], [4.274, 113.947], [4.595, 113.976], [4.017, 114.628],
  [4.395, 114.846], [4.729, 114.742], [4.916, 115.016], [4.405, 115.092], [4.314, 115.346],
  [4.909, 115.146]
];

function pointInPoly(lat: number, lon: number, poly: [number, number][]) {
  let inside = false;
  const n = poly.length;
  let p1x = poly[0][1], p1y = poly[0][0];
  for (let i = 0; i <= n; i++) {
    const p2x = poly[i % n][1], p2y = poly[i % n][0];
    if (lat > Math.min(p1y, p2y)) {
      if (lat <= Math.max(p1y, p2y)) {
        if (lon <= Math.max(p1x, p2x)) {
          let xinters = p1x;
          if (p1y !== p2y) {
            xinters = ((lat - p1y) * (p2x - p1x)) / (p2y - p1y) + p1x;
          }
          if (p1x === p2x || lon <= xinters) {
            inside = !inside;
          }
        }
      }
    }
    p1x = p2x;
    p1y = p2y;
  }
  return inside;
}

// ===================================================================
// FLAG COLOR FORMULAS
// ===================================================================
const SUDAN_BOUNDS = { minLat: 8.68, maxLat: 22.23, minLon: 21.81, maxLon: 38.60 };

function getSudanFlagColor(lat: number, lon: number): [number, number, number] {
  const u = (lon - SUDAN_BOUNDS.minLon) / (SUDAN_BOUNDS.maxLon - SUDAN_BOUNDS.minLon);
  const v = (lat - SUDAN_BOUNDS.minLat) / (SUDAN_BOUNDS.maxLat - SUDAN_BOUNDS.minLat);
  const x = u;
  const y = 1.0 - v;

  // Hoist Green Triangle on West/left (#007229)
  const triangleTip = 0.35;
  if (x <= triangleTip * (1.0 - 2.0 * Math.abs(y - 0.5))) {
    return [0.0, 0.447, 0.161];
  }

  // 3 Horizontal Stripes: Red, White, Black
  if (y < 0.333) {
    // Red (#D21034)
    return [0.824, 0.063, 0.204];
  } else if (y < 0.666) {
    // White (#FFFFFF)
    return [1.0, 1.0, 1.0];
  } else {
    // Black / Deep Obsidian (#1e1e22)
    return [0.12, 0.12, 0.135];
  }
}

const MALAY_PEN_BOUNDS = { minLat: 1.27, maxLat: 6.70, minLon: 100.14, maxLon: 104.28 };
const MALAY_BOR_BOUNDS = { minLat: 0.85, maxLat: 7.03, minLon: 109.53, maxLon: 119.28 };

function getMalaysiaFlagColor(lat: number, lon: number, isPeninsula: boolean): [number, number, number] {
  const b = isPeninsula ? MALAY_PEN_BOUNDS : MALAY_BOR_BOUNDS;
  const u = (lon - b.minLon) / (b.maxLon - b.minLon);
  const v = (lat - b.minLat) / (b.maxLat - b.minLat);
  const x = u;
  const y = 1.0 - v;

  // Top-left Canton
  if (x <= 0.55 && y <= 0.50) {
    const cx = 0.55 * 0.42;
    const cy = 0.50 * 0.42;
    const r = 0.14;
    const distCrescent = Math.hypot(x - cx, y - cy);
    const distStar = Math.hypot(x - (cx + r * 0.8), y - cy);
    if (distCrescent < r || distStar < r * 0.55) {
      // Yellow (#FFCC00) for Crescent & Star
      return [1.0, 0.80, 0.0];
    }
    // Royal Blue (#010066) Canton
    return [0.004, 0.0, 0.40];
  }

  // 14 alternating Red & White Stripes
  const stripe = Math.floor(y * 14);
  if (stripe % 2 === 0) {
    // Red (#CC0001)
    return [0.80, 0.0, 0.004];
  } else {
    // White (#FFFFFF)
    return [1.0, 1.0, 1.0];
  }
}

function latLonToVec3(lat: number, lon: number, r: number) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta)
  );
}

function facingRotation(v: THREE.Vector3) {
  const n = v.clone().normalize();
  return { y: -Math.atan2(n.x, n.z), x: Math.asin(n.y) };
}

// Helper to build country outline line loops
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function createCountryOutline(poly: [number, number][], color = 0xdfcbb2, opacity = 0.45) {
  const points: THREE.Vector3[] = [];
  for (const [lat, lon] of poly) {
    points.push(latLonToVec3(lat, lon, GLOBE_R * 1.006));
  }
  const geo = new THREE.BufferGeometry().setFromPoints(points);
  const mat = new THREE.LineBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false,
  });
  return { mesh: new THREE.LineLoop(geo, mat), geo, mat };
}

// Helper to build glowing city markers
function createCityMarker(lat: number, lon: number, labelColor = 0xffdfb3) {
  const group = new THREE.Group();
  const pos = latLonToVec3(lat, lon, GLOBE_R * 1.018);
  group.position.copy(pos);

  // Pin core sphere
  const coreGeo = new THREE.SphereGeometry(0.024, 16, 16);
  const coreMat = new THREE.MeshBasicMaterial({
    color: labelColor,
    transparent: true,
    opacity: 0, // revealed during intro
  });
  const core = new THREE.Mesh(coreGeo, coreMat);
  group.add(core);

  // Pulsing halo ring
  const haloGeo = new THREE.RingGeometry(0.034, 0.052, 32);
  const haloMat = new THREE.MeshBasicMaterial({
    color: labelColor,
    transparent: true,
    opacity: 0, // revealed during intro
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const halo = new THREE.Mesh(haloGeo, haloMat);
  halo.lookAt(pos.clone().multiplyScalar(2));
  group.add(halo);

  return { group, halo, coreMat, haloMat, coreGeo, haloGeo };
}

export default function HeroGlobe() {
  const containerRef = useRef<HTMLDivElement>(null);
  const skipRef = useRef<(() => void) | null>(null);
  const [introDone, setIntroDone] = useState(false);
  const t = useTranslations('HomePage.hero');

  // null = follow the site theme. 'day' / 'night' = user override from the globe button.
  // Held in a ref so the animation loop reads it without re-running the effect.
  const overrideRef = useRef<'day' | 'night' | null>(null);
  const [globeIsNight, setGlobeIsNight] = useState(false);
  const resyncRef = useRef<(() => void) | null>(null);


  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const isMobile =
      window.matchMedia('(pointer: coarse)').matches && window.innerWidth < 900;

    const QUALITY = { pixelRatio: 1.5 };

    // Dot size is derived from how large the globe actually renders, not from a
    // breakpoint. Dot spacing on screen scales with the globe's pixel radius,
    // so a fixed size packs the dots into a solid mass on small canvases.
    // BASE_SIZE_REF is the value tuned against SIZE_REFERENCE_PX; anything
    // smaller or larger scales from there.
    const BASE_SIZE_REF = 15.0;
    const SIZE_REFERENCE_PX = 361; // globe pixel radius on a ~1440x800 canvas
    // Floor and ceiling, so phones keep legible dots and very large screens
    // do not grow them without limit.
    const SIZE_SCALE_MIN = 0.5;
    const SIZE_SCALE_MAX = 1.15;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      30,
      container.clientWidth / container.clientHeight,
      0.1,
      100
    );
    camera.position.copy(CAM_NEAR);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, QUALITY.pixelRatio));
    container.appendChild(renderer.domElement);
    const canvas = renderer.domElement;

    const clock = new THREE.Clock();

    const globeGroup = new THREE.Group();
    scene.add(globeGroup);

    // Read initial theme
    const startsDark =
      (document.documentElement.getAttribute('data-theme') ??
        document.body.getAttribute('data-theme')) === 'dark';

    // DIRECTION A: Palette definition
    // ☀️ Sunlit Day: crisp warm white land, crystalline sky-cyan oceans, golden rim
    //const DAY_LAND_COLOR = new THREE.Color(0xF0E0C9);
    const DAY_LAND_COLOR = new THREE.Color(0xffffff);
    const DAY_OCEAN_COLOR = new THREE.Color(0x8eb5d9);
    const DAY_RIM_COLOR = new THREE.Color(0xf0dac2);
    const DAY_ARC_COLOR = new THREE.Color(0xfff7ea);
    const DAY_GLOW_COLOR = new THREE.Color(0xf5dfcc);

    // 🌙 Nocturnal Night: rich golden sand land, midnight indigo oceans, moonlit blue rim
    // const NIGHT_LAND_COLOR = new THREE.Color(0xdfcbb2); // beige
    const NIGHT_LAND_COLOR = new THREE.Color(0x000000); // black
    const NIGHT_OCEAN_COLOR = new THREE.Color(0x435773);
    const NIGHT_RIM_COLOR = new THREE.Color(0x8fb8d8);
    const NIGHT_ARC_COLOR = new THREE.Color(0xffe8c6);
    const NIGHT_GLOW_COLOR = new THREE.Color(0xead9c4);

        // Live colors the uniforms point at, plus the targets we ease toward.
    // Mutating `live` in place updates the uniforms without reallocating.
    const live = {
      land: (startsDark ? NIGHT_LAND_COLOR : DAY_LAND_COLOR).clone(),
      ocean: (startsDark ? NIGHT_OCEAN_COLOR : DAY_OCEAN_COLOR).clone(),
      rim: (startsDark ? NIGHT_RIM_COLOR : DAY_RIM_COLOR).clone(),
      arc: (startsDark ? NIGHT_ARC_COLOR : DAY_ARC_COLOR).clone(),
      glow: (startsDark ? NIGHT_GLOW_COLOR : DAY_GLOW_COLOR).clone(),
    };
    const target = {
      land: live.land.clone(),
      ocean: live.ocean.clone(),
      rim: live.rim.clone(),
      arc: live.arc.clone(),
      glow: live.glow.clone(),
    };

    // Effective mode = override if the user set one, otherwise the site theme.
    function resolveTarget() {
      const siteDark =
        (document.documentElement.getAttribute('data-theme') ??
          document.body.getAttribute('data-theme')) === 'dark';
      const dark = siteDark;

      target.land.copy(dark ? NIGHT_LAND_COLOR : DAY_LAND_COLOR);
      target.ocean.copy(dark ? NIGHT_OCEAN_COLOR : DAY_OCEAN_COLOR);
      target.rim.copy(dark ? NIGHT_RIM_COLOR : DAY_RIM_COLOR);
      target.arc.copy(dark ? NIGHT_ARC_COLOR : DAY_ARC_COLOR);
      target.glow.copy(dark ? NIGHT_GLOW_COLOR : DAY_GLOW_COLOR);
      setGlobeIsNight(dark);
    }
    resyncRef.current = resolveTarget;
    resolveTarget();

    // Watch the site theme. Toggling it clears any manual override so the
    // globe falls back in line with the rest of the page.
    const themeObserver = new MutationObserver(() => {
      overrideRef.current = null;
      resolveTarget();
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    // Invisible hit-testing sphere for smooth drag-to-rotate across full globe
    const hitGeo = new THREE.SphereGeometry(GLOBE_R, 32, 16);
    const hitMat = new THREE.MeshBasicMaterial({ visible: false });
    const hitSphere = new THREE.Mesh(hitGeo, hitMat);
    globeGroup.add(hitSphere);

    // Soft Horizon Rim to clearly delineate globe boundary
    const rimGeo = new THREE.SphereGeometry(GLOBE_R * 1.002, 64, 48);
    const rimMat = new THREE.ShaderMaterial({
      vertexShader: rimVertexShader,
      fragmentShader: rimFragmentShader,
      uniforms: {
        uColor: new THREE.Uniform(live.rim),
      },
      transparent: true,
      depthWrite: false,
    });
    const rimMesh = new THREE.Mesh(rimGeo, rimMat);
    globeGroup.add(rimMesh);

    // Vector boundary outlines for Sudan & Malaysia
    // commented out till i need it back again if we bring back the solid globe
    // const sudanOutline = createCountryOutline(SUDAN_POLY, 0xdfcbb2, 0.40);
    // const malayPenOutline = createCountryOutline(MALAYSIA_PEN, 0xdfcbb2, 0.40);
    // const malayBorOutline = createCountryOutline(MALAYSIA_BORNEO, 0xdfcbb2, 0.40);
    // globeGroup.add(sudanOutline.mesh, malayPenOutline.mesh, malayBorOutline.mesh);

    // Glowing city pin markers for Khartoum and Johor Bahru (UTM)
    const markerKhartoum = createCityMarker(KHARTOUM.lat, KHARTOUM.lon, 0xffe2b8);
    const markerJohor = createCityMarker(JOHOR_UTM.lat, JOHOR_UTM.lon, 0xffe2b8);
    markerKhartoum.group.visible = false;
    markerJohor.group.visible = false;
    globeGroup.add(markerKhartoum.group, markerJohor.group);

    // ===================================================================
    // 3D FLIGHT CONNECTION ARC (Khartoum ➔ Johor UTM)
    // ===================================================================
    const arcStart = latLonToVec3(KHARTOUM.lat, KHARTOUM.lon, GLOBE_R);
    const arcEnd = latLonToVec3(JOHOR_UTM.lat, JOHOR_UTM.lon, GLOBE_R);
    const arcMid = arcStart.clone().add(arcEnd).multiplyScalar(0.5)
      .normalize().multiplyScalar(GLOBE_R + 1.12);
    const arcCurve = new THREE.QuadraticBezierCurve3(arcStart, arcMid, arcEnd);

    const arcUniforms = {
      uProgress: new THREE.Uniform(0),
      uTime: new THREE.Uniform(0),
      uColor: new THREE.Uniform(live.arc),
      uIntensity: new THREE.Uniform(1.0),
    };

    const arcCoreGeometry = new THREE.TubeGeometry(arcCurve, ARC_SEGMENTS, 0.012, 32, false);
    const arcCoreMaterial = new THREE.ShaderMaterial({
      vertexShader: arcVertexShader,
      fragmentShader: arcCoreFragmentShader,
      uniforms: arcUniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const arcLine = new THREE.Mesh(arcCoreGeometry, arcCoreMaterial);
    globeGroup.add(arcLine);

    const arcGlowUniforms = {
      uProgress: arcUniforms.uProgress,
      uTime: arcUniforms.uTime,
      uColor: new THREE.Uniform(live.glow),
      uIntensity: new THREE.Uniform(0.55),
    };

    const arcGlowGeometry = new THREE.TubeGeometry(arcCurve, ARC_SEGMENTS, 0.032, 32, false);
    const arcGlowMaterial = new THREE.ShaderMaterial({
      vertexShader: arcVertexShader,
      fragmentShader: arcGlowFragmentShader,
      uniforms: arcGlowUniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const arcGlow = new THREE.Mesh(arcGlowGeometry, arcGlowMaterial);
    globeGroup.add(arcGlow);

    // Core Point Cloud System
    const pointGeo = new THREE.BufferGeometry();
    const pointMat = new THREE.ShaderMaterial({
      vertexShader: pointVertexShader,
      fragmentShader: pointFragmentShader,
      uniforms: {
        uPixelRatio: new THREE.Uniform(Math.min(window.devicePixelRatio, QUALITY.pixelRatio)),
        uBaseSize: new THREE.Uniform(BASE_SIZE_REF),
        uTime: new THREE.Uniform(0),
        uLandColor: new THREE.Uniform(live.land),
        uOceanColor: new THREE.Uniform(live.ocean),
      },
      transparent: true,
      depthWrite: false,
    });
    const pointMesh = new THREE.Points(pointGeo, pointMat);
    globeGroup.add(pointMesh);

    // Load precomputed latitude/longitude grid points and inject dense flag-tinted dots
    fetch('/data/globe-points.json')
      .then((res) => res.json())
      .then((data: { land: [number, number][]; ocean: [number, number][] }) => {
        const rawLand = data.land || [];
        const rawOcean = data.ocean || [];

        // 1. Separate base land points outside Sudan & Malaysia
        const standardLand: [number, number][] = [];
        for (let i = 0; i < rawLand.length; i++) {
          const [lat, lon] = rawLand[i];
          const inSudanBox = lat >= 8.6 && lat <= 22.3 && lon >= 21.8 && lon <= 38.7;
          if (inSudanBox && pointInPoly(lat, lon, SUDAN_POLY)) continue;

          const inMalayPenBox = lat >= 1.2 && lat <= 6.8 && lon >= 100.0 && lon <= 104.5;
          if (inMalayPenBox && pointInPoly(lat, lon, MALAYSIA_PEN)) continue;

          const inMalayBorBox = lat >= 0.8 && lat <= 7.2 && lon >= 109.4 && lon <= 119.4;
          if (inMalayBorBox && pointInPoly(lat, lon, MALAYSIA_BORNEO)) continue;

          standardLand.push([lat, lon]);
        }

        // 2. Generate high-definition flag dots inside Sudan
        const flagDots: { lat: number; lon: number; color: [number, number, number] }[] = [];
        const SUDAN_ROWS = 28;
        for (let i = 0; i < SUDAN_ROWS; i++) {
          const lat = 8.8 + (13.3 * i) / (SUDAN_ROWS - 1);
          for (let j = 0; j < SUDAN_ROWS; j++) {
            const lon = 22.0 + (16.4 * j) / (SUDAN_ROWS - 1);
            if (pointInPoly(lat, lon, SUDAN_POLY)) {
              flagDots.push({ lat, lon, color: getSudanFlagColor(lat, lon) });
            }
          }
        }

        // 3. Generate high-definition flag dots inside Malaysia (Peninsula + Borneo)
        const PEN_STEPS = 20;
        for (let i = 0; i < PEN_STEPS; i++) {
          const lat = 1.3 + (5.3 * i) / (PEN_STEPS - 1);
          for (let j = 0; j < PEN_STEPS; j++) {
            const lon = 100.2 + (4.0 * j) / (PEN_STEPS - 1);
            if (pointInPoly(lat, lon, MALAYSIA_PEN)) {
              flagDots.push({ lat, lon, color: getMalaysiaFlagColor(lat, lon, true) });
            }
          }
        }

        const BOR_STEPS = 22;
        for (let i = 0; i < BOR_STEPS; i++) {
          const lat = 0.9 + (6.1 * i) / (BOR_STEPS - 1);
          for (let j = 0; j < BOR_STEPS; j++) {
            const lon = 109.6 + (9.6 * j) / (BOR_STEPS - 1);
            if (pointInPoly(lat, lon, MALAYSIA_BORNEO)) {
              flagDots.push({ lat, lon, color: getMalaysiaFlagColor(lat, lon, false) });
            }
          }
        }

        const totalPoints = standardLand.length + flagDots.length + rawOcean.length;

        const positions = new Float32Array(totalPoints * 3);
        const normals = new Float32Array(totalPoints * 3);
        const types = new Float32Array(totalPoints);
        const isFlags = new Float32Array(totalPoints);
        const flagColors = new Float32Array(totalPoints * 3);

        let idx = 0;

        // A. Standard Land Points (Theme colored)
        for (let i = 0; i < standardLand.length; i++) {
          const [lat, lon] = standardLand[i];
          const v = latLonToVec3(lat, lon, GLOBE_R);
          positions[idx * 3] = v.x;
          positions[idx * 3 + 1] = v.y;
          positions[idx * 3 + 2] = v.z;

          const n = v.clone().normalize();
          normals[idx * 3] = n.x;
          normals[idx * 3 + 1] = n.y;
          normals[idx * 3 + 2] = n.z;

          types[idx] = 1.0;
          isFlags[idx] = 0.0;
          idx++;
        }

        // B. Flag Tinted Dots (Sudan & Malaysia)
        for (let i = 0; i < flagDots.length; i++) {
          const { lat, lon, color } = flagDots[i];
          const v = latLonToVec3(lat, lon, GLOBE_R * 1.002);
          positions[idx * 3] = v.x;
          positions[idx * 3 + 1] = v.y;
          positions[idx * 3 + 2] = v.z;

          const n = v.clone().normalize();
          normals[idx * 3] = n.x;
          normals[idx * 3 + 1] = n.y;
          normals[idx * 3 + 2] = n.z;

          types[idx] = 1.0;
          isFlags[idx] = 1.0;
          flagColors[idx * 3] = color[0];
          flagColors[idx * 3 + 1] = color[1];
          flagColors[idx * 3 + 2] = color[2];
          idx++;
        }

        // C. Ocean Micro-Dots
        for (let i = 0; i < rawOcean.length; i++) {
          const [lat, lon] = rawOcean[i];
          const v = latLonToVec3(lat, lon, GLOBE_R);
          positions[idx * 3] = v.x;
          positions[idx * 3 + 1] = v.y;
          positions[idx * 3 + 2] = v.z;

          const n = v.clone().normalize();
          normals[idx * 3] = n.x;
          normals[idx * 3 + 1] = n.y;
          normals[idx * 3 + 2] = n.z;

          types[idx] = 0.0;
          isFlags[idx] = 0.0;
          idx++;
        }

        pointGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        pointGeo.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
        pointGeo.setAttribute('aType', new THREE.BufferAttribute(types, 1));
        pointGeo.setAttribute('aIsFlag', new THREE.BufferAttribute(isFlags, 1));
        pointGeo.setAttribute('aFlagColor', new THREE.BufferAttribute(flagColors, 3));
        pointGeo.computeBoundingSphere();
        if (!skipped) {
          clock.start();
        }
      })
      .catch((err) => {
        console.error('Failed to load globe points data:', err);
      });

    // Intro choreography targets
    const ROT_SUDAN = facingRotation(latLonToVec3(KHARTOUM.lat, KHARTOUM.lon, 1));
    const midDir = latLonToVec3(KHARTOUM.lat, KHARTOUM.lon, 1)
      .normalize()
      .add(latLonToVec3(JOHOR_UTM.lat, JOHOR_UTM.lon, 1).normalize());
    const ROT_ARC = facingRotation(midDir);

    // Initial position: Zoomed in on Sudan
    globeGroup.rotation.set(ROT_SUDAN.x, ROT_SUDAN.y, 0);

    // Drag-to-rotate interaction
    const raycaster = new THREE.Raycaster();
    const pointerNDC = new THREE.Vector2();
    let dragActive = false;
    let lastX = 0,
      lastY = 0,
      rotVelX = 0,
      rotVelY = 0,
      lastInteractTime = 0;
    let settled = false;
    let skipped = false;

    function hitsGlobe(x: number, y: number) {
      const rect = canvas.getBoundingClientRect();
      pointerNDC.x = ((x - rect.left) / rect.width) * 2 - 1;
      pointerNDC.y = -((y - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointerNDC, camera);
      return raycaster.intersectObject(hitSphere, false).length > 0;
    }

    function onDragStart(x: number, y: number) {
      if (!settled || !hitsGlobe(x, y)) return;
      dragActive = true;
      lastX = x;
      lastY = y;
      canvas.style.cursor = 'grabbing';
    }

    function onDragMove(x: number, y: number) {
      if (!dragActive) return;
      const dx = x - lastX;
      const dy = y - lastY;
      lastX = x;
      lastY = y;
      globeGroup.rotation.y += dx * 0.006;
      globeGroup.rotation.x = Math.max(
        -1.1,
        Math.min(1.1, globeGroup.rotation.x + dy * 0.004)
      );
      rotVelX = dy * 0.004;
      rotVelY = dx * 0.006;
      lastInteractTime = performance.now();
    }

    function onDragEnd() {
      if (!dragActive) return;
      dragActive = false;
      lastInteractTime = performance.now();
      canvas.style.cursor = 'grab';
    }

    const onMouseDown = (e: MouseEvent) => onDragStart(e.clientX, e.clientY);
    const onMouseMove = (e: MouseEvent) => onDragMove(e.clientX, e.clientY);
    const onMouseUp = () => onDragEnd();
    const onHoverMove = (e: MouseEvent) => {
      if (dragActive || !settled) return;
      canvas.style.cursor = hitsGlobe(e.clientX, e.clientY) ? 'grab' : 'default';
    };

    let touchStartX = 0;
    let touchStartY = 0;
    let touchDecided = false;
    let touchIsHorizontal = false;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchDecided = false;
        touchIsHorizontal = false;
        onDragStart(touchStartX, touchStartY);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1 || !dragActive) return;
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;

      // Discriminate between intentional horizontal globe rotation vs vertical page scrolling
      if (!touchDecided) {
        const totalDx = Math.abs(x - touchStartX);
        const totalDy = Math.abs(y - touchStartY);
        if (totalDx > 6 || totalDy > 6) {
          touchDecided = true;
          touchIsHorizontal = totalDx >= totalDy;
          if (!touchIsHorizontal) {
            // User is scrolling the page vertically! Release drag immediately so the page scrolls cleanly
            dragActive = false;
            rotVelX = 0;
            rotVelY = 0;
            return;
          }
        } else {
          return;
        }
      }

      if (touchIsHorizontal) {
        const dx = x - lastX;
        lastX = x;
        lastY = y;
        globeGroup.rotation.y += dx * 0.006;
        rotVelY = dx * 0.006;
        rotVelX = 0;
        lastInteractTime = performance.now();
      }
    };

    const onTouchEnd = () => {
      touchDecided = false;
      touchIsHorizontal = false;
      onDragEnd();
    };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onHoverMove);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('touchstart', onTouchStart, { passive: true });
    canvas.addEventListener('touchmove', onTouchMove, { passive: true });
    canvas.addEventListener('touchend', onTouchEnd);



    // Skip intro feature
    function skipIntro() {
      if (skipped) return;
      skipped = true;
      camera.position.copy(CAM_FAR);
      camera.lookAt(0, 0, 0);
      globeGroup.rotation.set(ROT_ARC.x, ROT_ARC.y, 0);
      arcUniforms.uProgress.value = 1.0;
      markerKhartoum.group.visible = true;
      markerJohor.group.visible = true;
      markerKhartoum.coreMat.opacity = 1.0;
      markerKhartoum.haloMat.opacity = 0.65;
      markerJohor.coreMat.opacity = 1.0;
      markerJohor.haloMat.opacity = 0.65;
      settled = true;
      setIntroDone(true);
      window.dispatchEvent(new Event('hero-intro-done'));
      sessionStorage.setItem('heroIntroDone', 'true');
    }
    skipRef.current = skipIntro;

    if (sessionStorage.getItem('heroIntroDone') === 'true') {
      skipIntro();
    }



    let isVisible = true;
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    observer.observe(container);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !settled && !skipped) {
        clock.start();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    let frameId: number;
    let lastFrameTime = performance.now();

    function animate() {
      frameId = requestAnimationFrame(animate);
      if (!isVisible) return;

      const elapsed = skipped ? T4 + 1 : clock.getElapsedTime();

      // Traveling pulse along flight arc
      arcUniforms.uTime.value = clock.getElapsedTime();
      arcGlowUniforms.uTime.value = clock.getElapsedTime();
      pointMat.uniforms.uTime.value = clock.getElapsedTime();



      // Compute hemisphere facing for Sudan & Malaysia to prevent back-side bleed
      const khartoumPos = new THREE.Vector3();
      markerKhartoum.group.getWorldPosition(khartoumPos);
      const kFacing = khartoumPos.dot(camera.position);
      const kFade = THREE.MathUtils.clamp(
        (kFacing / (GLOBE_R * camera.position.length())) * 4.0 + 0.15,
        0,
        1
      );

      const johorPos = new THREE.Vector3();
      markerJohor.group.getWorldPosition(johorPos);
      const jFacing = johorPos.dot(camera.position);
      const jFade = THREE.MathUtils.clamp(
        (jFacing / (GLOBE_R * camera.position.length())) * 4.0 + 0.15,
        0,
        1
      );

      // Outlines fade out as the camera closes in, because the flag dots merge
      // into a solid shape at that range and the border line becomes a stripe
      // laid over it. Thresholds mirror the merge range in pointVertexShader.
      // const camDist = camera.position.length();
      // const outlineFade = THREE.MathUtils.smoothstep(camDist, 3.2, 6.8);

      // // Sudan boundary & Khartoum marker culling
      // sudanOutline.mesh.visible = kFade > 0.01 && outlineFade > 0.01;
      // sudanOutline.mat.opacity = 0.40 * kFade * outlineFade;

      // // Malaysia boundaries & Johor marker culling
      // malayPenOutline.mesh.visible = jFade > 0.01 && outlineFade > 0.01;
      // malayPenOutline.mat.opacity = 0.40 * jFade * outlineFade;
      // malayBorOutline.mesh.visible = jFade > 0.01 && outlineFade > 0.01;
      // malayBorOutline.mat.opacity = 0.40 * jFade * outlineFade;

      // 4-Phase Cinematic Intro Sequence
      if (elapsed < T1) {
        // Phase 1: Tight shot hold on Sudan
      } else if (elapsed < T2) {
        // Phase 2: Pullback from tight shot to full globe
        const p = easeInOutCubic((elapsed - T1) / T_PULLBACK);
        camera.position.lerpVectors(CAM_NEAR, CAM_FAR, p);
        camera.lookAt(0, 0, 0);
      } else if (elapsed < T3) {
        // Phase 3: Smooth rotation from Sudan to Sudan–Malaysia flight midpoint
        camera.position.copy(CAM_FAR);
        camera.lookAt(0, 0, 0);
        const p = easeInOutSine((elapsed - T2) / T_TURN);
        globeGroup.rotation.y = THREE.MathUtils.lerp(ROT_SUDAN.y, ROT_ARC.y, p);
        globeGroup.rotation.x = THREE.MathUtils.lerp(ROT_SUDAN.x, ROT_ARC.x, p);
      } else if (elapsed < T4) {
        // Phase 4: Flight connection arc draws across the ocean
        const p = easeOutExpo((elapsed - T3) / T_ARC);
        arcUniforms.uProgress.value = p;
        markerKhartoum.group.visible = kFade > 0.01;
        markerJohor.group.visible = jFade > 0.01;
        markerKhartoum.coreMat.opacity = Math.min(1.0, p * 2.0) * kFade;
        markerKhartoum.haloMat.opacity = Math.min(0.65, p * 1.3) * kFade;
        markerJohor.coreMat.opacity = Math.min(1.0, p * 2.0) * jFade;
        markerJohor.haloMat.opacity = Math.min(0.65, p * 1.3) * jFade;
      } else if (!settled) {
        // Settled: Trigger completion
        settled = true;
        arcUniforms.uProgress.value = 1.0;
        markerKhartoum.group.visible = kFade > 0.01;
        markerJohor.group.visible = jFade > 0.01;
        markerKhartoum.coreMat.opacity = 1.0 * kFade;
        markerKhartoum.haloMat.opacity = 0.65 * kFade;
        markerJohor.coreMat.opacity = 1.0 * jFade;
        markerJohor.haloMat.opacity = 0.65 * jFade;
        setIntroDone(true);
        window.dispatchEvent(new Event('hero-intro-done'));
        sessionStorage.setItem('heroIntroDone', 'true');
      } else {
        // Settled: continuous update during drag & auto-rotation
        markerKhartoum.group.visible = kFade > 0.01;
        markerJohor.group.visible = jFade > 0.01;
        markerKhartoum.coreMat.opacity = 1.0 * kFade;
        markerKhartoum.haloMat.opacity = 0.65 * kFade;
        markerJohor.coreMat.opacity = 1.0 * jFade;
        markerJohor.haloMat.opacity = 0.65 * jFade;
      }

      // Subtle breathing pulse for city markers
      const pulse = 1.0 + 0.15 * Math.sin(clock.getElapsedTime() * 3.5);
      markerKhartoum.halo.scale.setScalar(pulse);
      markerJohor.halo.scale.setScalar(pulse);

      // Idle auto-rotation
      if (settled) {
        const idle = performance.now() - lastInteractTime > RESUME_DELAY;
        if (!dragActive && idle) {
          globeGroup.rotation.y += 0.0016;
          rotVelX *= 0.92;
          rotVelY *= 0.92;
        } else if (!dragActive) {
          globeGroup.rotation.y += rotVelY;
          globeGroup.rotation.x += rotVelX;
          rotVelX *= 0.92;
          rotVelY *= 0.92;
        }
      }

            // Frame-rate independent ease toward the target palette (~0.3s time constant),
      // so a theme change fades instead of snapping.
      const nowMs = performance.now();
      const dt = Math.min(0.05, (nowMs - lastFrameTime) / 1000);
      lastFrameTime = nowMs;
      const k = 1 - Math.pow(0.05, dt);
      live.land.lerp(target.land, k);
      live.ocean.lerp(target.ocean, k);
      live.rim.lerp(target.rim, k);
      live.arc.lerp(target.arc, k);
      live.glow.lerp(target.glow, k);

      renderer.render(scene, camera);
    }
    animate();

    // Responsive FOV management
    // Increased FOV gives the globe more vertical breathing room to prevent cropping
    const WIDE_FOV = 25;
    const NARROW_FOV = 15;
    const WIDE_ASPECT = 16 / 9;
    const NARROW_ASPECT = 0.6;

    function applyFov(aspect: number) {
      const tRaw = (aspect - NARROW_ASPECT) / (WIDE_ASPECT - NARROW_ASPECT);
      const tClamped = Math.max(0, Math.min(1, tRaw));
      const refFov = THREE.MathUtils.lerp(NARROW_FOV, WIDE_FOV, easeInOutSine(tClamped));

      if (aspect >= WIDE_ASPECT) {
        camera.fov = refFov;
      } else {
        const refHalf = THREE.MathUtils.degToRad(refFov) / 2;
        const widthAtRef = Math.tan(refHalf) * WIDE_ASPECT;
        camera.fov = THREE.MathUtils.radToDeg(Math.atan(widthAtRef / aspect)) * 2;
      }
    }

    let lastW = 0;
    let lastH = 0;

    function handleResize() {
      if (!container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;

      // On mobile devices, address bar collapsing/expanding triggers window resize with identical width.
      // Ignore height-only fluctuations under 140px to prevent the globe from jumping/re-centering mid-scroll.
      if (isMobile && w === lastW && Math.abs(h - lastH) < 140) {
        return;
      }
      lastW = w;
      lastH = h;

      const aspect = w / h;
      camera.aspect = aspect;
      applyFov(aspect);
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);

      // Globe's rendered pixel radius at the settled camera distance. Measured
      // after applyFov, because a narrow aspect widens the FOV and shrinks the
      // globe. CAM_FAR is used rather than the live camera position so the dots
      // do not change size during the intro's pullback.
      const halfFov = THREE.MathUtils.degToRad(camera.fov) / 2;
      const globePx = (GLOBE_R / (Math.tan(halfFov) * CAM_FAR.z)) * (h / 2);
      const sizeScale = THREE.MathUtils.clamp(
        globePx / SIZE_REFERENCE_PX,
        SIZE_SCALE_MIN,
        SIZE_SCALE_MAX
      );
      pointMat.uniforms.uBaseSize.value = BASE_SIZE_REF * sizeScale;
    }
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      cancelAnimationFrame(frameId);

      skipRef.current = null;
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onHoverMove);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
      observer.disconnect();
      themeObserver.disconnect();
      resyncRef.current = null;
      hitGeo.dispose();
      hitMat.dispose();
      rimGeo.dispose();
      rimMat.dispose();
      pointGeo.dispose();
      pointMat.dispose();
      // sudanOutline.geo.dispose();
      // sudanOutline.mat.dispose();
      // malayPenOutline.geo.dispose();
      // malayPenOutline.mat.dispose();
      // malayBorOutline.geo.dispose();
      // malayBorOutline.mat.dispose();
      markerKhartoum.coreGeo.dispose();
      markerKhartoum.coreMat.dispose();
      markerKhartoum.haloGeo.dispose();
      markerKhartoum.haloMat.dispose();
      markerJohor.coreGeo.dispose();
      markerJohor.coreMat.dispose();
      markerJohor.haloGeo.dispose();
      markerJohor.haloMat.dispose();
      arcCoreGeometry.dispose();
      arcCoreMaterial.dispose();
      arcGlowGeometry.dispose();
      arcGlowMaterial.dispose();
      renderer.dispose();
      container.removeChild(renderer.domElement);
    };
  }, []);



  return (
    <div className={`${styles.globeWrapper} ${introDone ? styles.shifted : ''}`}>
      <div ref={containerRef} className={styles.globeContainer} />
      {!introDone && (
        <button
          className={styles.skipButton}
          onClick={() => skipRef.current?.()}
        >
          {t('skip')}
        </button>
      )}
    </div>
  );
}